declare const I18n;

import React, { useState, useEffect } from "react";
import api from "../../../../api";

type QuizOption = {
  id: number;
  text: string;
  correct: boolean;
};

type QuizQuestion = {
  id: number;
  text: string;
  completed: boolean;
  options: QuizOption[];
};

type ShowQuizProps = {
  quizQuestions?: QuizQuestion[];
  completed?: boolean;
  courseId: string;
  lessonId: string;
  isFacilitator: boolean;
  onNextLesson: () => void;
  onPreviousLesson: () => void;
};

const ShowQuiz: React.FC<ShowQuizProps> = ({ quizQuestions = [], courseId, lessonId, isFacilitator = false, onNextLesson, onPreviousLesson }) => {
  const [answers, setAnswers] = useState<{ [key: number]: number | null }>({});
  const [errorMessage, setErrorMessage] = useState("");
  const [showAnswerForQuestion, setShowAnswerForQuestion] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [questionsToShow, setQuestionsToShow] = useState<QuizQuestion[]>([]);

  useEffect(() => {
    const allCompleted = quizQuestions.every(q => q.completed === true);
    if (allCompleted) { setQuizCompleted(true) }

    const firstIncompleteIndex = quizQuestions.findIndex(q => !q.completed);

    if (firstIncompleteIndex > -1) {
      setCurrentQuestionIndex(firstIncompleteIndex)
    }

    setAnswers(prev => {
      const updated = { ...prev };

      quizQuestions.forEach(q => {
        if (q.completed) {
          const correctOpt = q.options.find(opt => opt.correct);
          if (correctOpt) {
            updated[q.id] = correctOpt.id;
          }
        }
      });

      return updated;
    });

  }, [quizQuestions]);

  useEffect(() => {
    if (quizCompleted) {
      setQuestionsToShow(quizQuestions);
    } else {
      setQuestionsToShow(quizQuestions[currentQuestionIndex] ? [quizQuestions[currentQuestionIndex]] : []);
    }
  }, [quizCompleted, currentQuestionIndex, quizQuestions]);

  // Avoid rendering until questions are loaded
  if (questionsToShow.length === 0) {
    return null;
  }

  const handleChange = (questionId: number, optionId: number) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: optionId,
    }));

    setShowAnswerForQuestion(false);
    setErrorMessage("");
  };

  const handleSubmit = () => {
    setShowAnswerForQuestion(true);

    const question = questionsToShow[0];
    const selected = answers[question.id];
    const correctOption = question.options.find((o) => o.correct);

    api.incrementQuizQuestionAttempts(question.id, selected)

    if (!selected) {
      setErrorMessage("Please select an answer");
    } else if (selected !== correctOption?.id) {
      setErrorMessage(I18n.t('models.quiz.messaging.incorrect'));
    } else {
      setErrorMessage("");
    }
  };

  const handleNext = () => {
    if (quizQuestions.length > currentQuestionIndex + 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      setQuizCompleted(true);
    }

    setShowAnswerForQuestion(false);
    setErrorMessage("");
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
      setShowAnswerForQuestion(false);
      setErrorMessage("");
    }
  };

  const handleCompleteLesson = async () => {
    if (isFacilitator) {
      await api.submitLessonFacilitator(courseId, lessonId)
    } else {
      await api.submitLesson(courseId, lessonId)
    }
    onNextLesson()
  }

  if (!quizCompleted) {
    const currentQuestion = questionsToShow[0];
    const hasSelectedAnswer = !!answers[currentQuestion.id];
    const selectedAnswer = answers[currentQuestion.id];
    const correctOption = currentQuestion.options.find((o) => o.correct);
    const isCorrect = currentQuestion.completed || (showAnswerForQuestion && selectedAnswer === correctOption?.id);

    return (
      <>
        <h3 style={{ paddingBottom: "1rem" }}>{I18n.t("models.quiz.messaging.answer_question_below")}</h3>

        <div className="quiz_box">
          <div className="quiz_question">
            <p>
              <strong>{currentQuestionIndex + 1}. {currentQuestion.text}</strong>
            </p>

            {currentQuestion.options.map((opt) => {
              const isSelected = answers[currentQuestion.id] === opt.id;

              return (
                <label key={opt.id} className="question_labels">
                  <input
                    type="radio"
                    className="form-check-input"
                    name={`question-${currentQuestion.id}`}
                    checked={isSelected}
                    onChange={() => handleChange(currentQuestion.id, opt.id)}
                    disabled={isCorrect}
                  />

                  <span style={{ marginLeft: "8px", fontWeight: "normal" }}>
                    {opt.text}
                  </span>

                  {/* Only show checkmark after submit */}
                  {isCorrect && isSelected && (
                    <span className="text-success" style={{ marginLeft: "8px" }}>
                      <i className="fa-solid fa-circle-check"></i>
                    </span>
                  )}
                </label>
              );
            })}

            {errorMessage && (
              <p className="text-danger" style={{ marginTop: "0.5rem" }}>
                {errorMessage}
              </p>
            )}
          </div>
          <div className="d-flex mt-3">
            {currentQuestionIndex === 0 && (
              <button
                className="btn next_previous_buttons"
                onClick={onPreviousLesson}
              >
                <i className="fa-solid fa-angle-left"></i>
                {I18n.t("views.common.prev_lesson")}
              </button>
            )}
            {currentQuestionIndex > 0 && (
              <button
                className="btn next_previous_buttons"
                onClick={handlePrevious}
              >
                <i className="fa-solid fa-angle-left"></i>
                {I18n.t("views.common.prev")}
              </button>
            )}
            {isCorrect ? (
            <button
              className="btn next_previous_buttons ms-auto"
              onClick={handleNext}
            >
              {I18n.t("views.common.next")} <i className="fa-solid fa-angle-right"></i>
            </button>
            ) : (
            <button
              className="btn next_previous_buttons ms-auto"
              onClick={handleSubmit}
              disabled={!hasSelectedAnswer}
            >
              {I18n.t("views.forms.submit")}
            </button>
          )}
          </div>
        </div>
        {isFacilitator && (
          <div className="d-flex mt-3">
            <button
              className="btn next_previous_buttons ms-auto"
              onClick={handleCompleteLesson}
            >
              {I18n.t("models.lesson.messaging.show")}
              <i className="fa-solid fa-angle-right"></i>
            </button>
          </div>
        )}

      </>
    );
  }

  return (
    <>
      <h3 style={{ paddingBottom: "1rem" }}>{I18n.t("models.quiz.messaging.quiz_questions")}</h3>

      <div className="quiz_box">
        {questionsToShow.map((question, index) => {
          const correctOption = question.options.find((o) => o.correct);
          return (
            <div key={question.id} className="quiz_question">
              <p>
                <strong>{index + 1}. {question.text}</strong>
              </p>

              {question.options.map((opt) => {
                const isCorrectOption = opt.id === correctOption?.id;

                return (
                  <label key={opt.id} className="question_labels">
                    <input
                      type="radio"
                      className="form-check-input"
                      checked={isCorrectOption}
                      disabled={true}
                    />

                    <span style={{ marginLeft: "8px", fontWeight: "normal" }}>
                      {opt.text}
                    </span>

                    {isCorrectOption && (
                      <span className="text-success" style={{ marginLeft: "8px" }}>
                        <i className="fa-solid fa-circle-check"></i>
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
          );
        })}
      </div>
      <div className="d-flex mt-3">
        <button
          className="btn next_previous_buttons"
          onClick={onPreviousLesson}
        >
          <i className="fa-solid fa-angle-left"></i>
          {I18n.t("views.common.prev_lesson")}
        </button>
        <button
          className="btn next_previous_buttons ms-auto"
          onClick={handleCompleteLesson}
        >
          {I18n.t("models.lesson.messaging.show")}
          <i className="fa-solid fa-angle-right"></i>
        </button>
      </div>

    </>
  );
};

export default ShowQuiz;
